import { createApi } from '@reduxjs/toolkit/query/react';
import {
  removeNode,
  insertChildNode,
  insertBeforeNode,
  insertAfterNode,
  updateNode,
  findNode,
} from 'components/organisms/folderTree/treeHelpers';
import deserialize from 'helpers/getroPropertyMapper';
import decodeString from 'helpers/decodeString';
import { contactsApi } from 'services/contacts';
import { networkBaseQueryV2 } from './queries';

const LIST_TREE_TAG = ['list-tree'];

// Mirror backend semantics: reverse-iterate so input order maps to top-to-bottom
// in the destination container. Exported for direct testing — see
// __tests__/applyBulkMoveToTree.test.js.
export const applyBulkMoveToTree = (tree, listIds, parentId) => {
  let working = tree;
  [...listIds].reverse().forEach((id) => {
    const dragged = findNode(working, id, 'list');
    if (!dragged) return;
    const pruned = removeNode(working, id, 'list');
    working = parentId == null ? [dragged, ...pruned] : insertChildNode(pruned, parentId, 'folder', dragged);
  });
  return working;
};

export const applyBulkDestroyToTree = (tree, listIds) =>
  listIds.reduce((working, id) => removeNode(working, id, 'list'), tree);

export const removeSharedListsFromCache = (draft, listIds) => {
  if (!draft?.sharedLists) return;

  const idSet = new Set(listIds);
  const nextSharedLists = draft.sharedLists.filter((list) => !idSet.has(list.id));
  const removedCount = draft.sharedLists.length - nextSharedLists.length;

  Object.assign(draft, {
    sharedLists: nextSharedLists,
    meta: {
      ...draft.meta,
      sharedListTotal: Math.max(0, (draft.meta?.sharedListTotal || 0) - removedCount),
    },
  });
};

// Walk the tree and return "parent / child / leaf" for a folder id, matching the
// backend's folder_path format on /contacts/shared_lists.
export const buildFolderPath = (tree, folderId) => {
  if (folderId == null || !Array.isArray(tree)) return '';
  const walk = (nodes, trail) =>
    nodes.reduce((found, node) => {
      if (found || node.type !== 'folder') return found;
      const nextTrail = [...trail, node.name];
      if (node.id === folderId) return nextTrail.join(' / ');
      if (Array.isArray(node.children) && node.children.length) {
        return walk(node.children, nextTrail);
      }
      return found;
    }, null);
  return walk(tree, []) || '';
};

const decodeNodeNames = (nodes) =>
  nodes.map((node) => ({
    ...node,
    name: decodeString(node.name),
    ...(node.children ? { children: decodeNodeNames(node.children) } : {}),
  }));

export const listsTreeApi = createApi({
  reducerPath: 'listsTreeApi',
  baseQuery: networkBaseQueryV2,
  tagTypes: LIST_TREE_TAG,
  endpoints: (builder) => ({
    getListTree: builder.query({
      query: ({ networkId }) => ({ url: `shared_lists/tree`, networkId }),
      providesTags: LIST_TREE_TAG,
      transformResponse: (response) => decodeNodeNames(response || []),
    }),
    createFolder: builder.mutation({
      query: ({ name, networkId, parentId = null }) => ({
        url: `shared_list_folders`,
        networkId,
        method: 'POST',
        data: { name, parentId },
      }),
      invalidatesTags: (result, error) => (error ? LIST_TREE_TAG : []),
      async onQueryStarted({ name, networkId, parentId = null }, { dispatch, queryFulfilled }) {
        const tempId = `tmp-${Date.now()}`;
        const patch = dispatch(
          listsTreeApi.util.updateQueryData('getListTree', { networkId }, (draft) => {
            const newFolder = { id: tempId, name, type: 'folder', children: [] };
            const updated =
              parentId == null ? [newFolder, ...draft] : insertChildNode(draft, parentId, 'folder', newFolder);
            return updated;
          }),
        );
        try {
          const { data } = await queryFulfilled;
          const realId = deserialize(data)?.id;

          if (realId != null) {
            const updateFn = (draft) => updateNode(draft, tempId, 'folder', () => ({ id: realId }));
            dispatch(listsTreeApi.util.updateQueryData('getListTree', { networkId }, updateFn));
          }
        } catch {
          patch.undo();
        }
      },
    }),
    updateFolder: builder.mutation({
      query: ({ folderId, name, networkId }) => ({
        url: `shared_list_folders/${folderId}`,
        method: 'PATCH',
        networkId,
        data: { name },
      }),
      invalidatesTags: (result, error) => (error ? LIST_TREE_TAG : []),
      async onQueryStarted({ folderId, name, networkId }, { dispatch, queryFulfilled }) {
        const updateFn = (draft) => updateNode(draft, folderId, 'folder', () => ({ name }));
        const patch = dispatch(listsTreeApi.util.updateQueryData('getListTree', { networkId }, updateFn));
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    moveFolder: builder.mutation({
      query: ({ folderId, parentId, beforeId, afterId, networkId }) => ({
        url: `shared_list_folders/${folderId}/move`,
        networkId,
        method: 'PATCH',
        data: { parentId, beforeId, afterId },
      }),
      invalidatesTags: (result, error) => (error ? LIST_TREE_TAG : []),
      async onQueryStarted({ folderId, parentId, beforeId, afterId, networkId }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          listsTreeApi.util.updateQueryData('getListTree', { networkId }, (draft) => {
            const dragged = findNode(draft, folderId, 'folder');
            if (!dragged) return draft;

            const pruned = removeNode(draft, folderId, 'folder');
            let updated = pruned;
            if (beforeId != null) {
              const target = findNode(pruned, beforeId, null);
              if (target) updated = insertBeforeNode(pruned, beforeId, target.type, dragged);
            } else if (afterId != null) {
              const target = findNode(pruned, afterId, null);
              if (target) updated = insertAfterNode(pruned, afterId, target.type, dragged);
            } else {
              updated = parentId == null ? [dragged, ...pruned] : insertChildNode(pruned, parentId, 'folder', dragged);
            }
            return updated;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    moveListToFolder: builder.mutation({
      query: ({ listId, folderId, beforeId, afterId, networkId }) => ({
        url: `shared_lists/${listId}/move_to_folder`,
        networkId,
        method: 'PATCH',
        data: { parentId: folderId, beforeId, afterId },
      }),
      invalidatesTags: (result, error) => (error ? LIST_TREE_TAG : []),
      async onQueryStarted({ listId, folderId, beforeId, afterId, networkId }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          listsTreeApi.util.updateQueryData('getListTree', { networkId }, (draft) => {
            const dragged = findNode(draft, listId, 'list');
            if (!dragged) return draft;

            const pruned = removeNode(draft, listId, 'list');
            let updated = pruned;
            if (beforeId != null) {
              const target = findNode(pruned, beforeId, null);
              if (target) updated = insertBeforeNode(pruned, beforeId, target.type, dragged);
            } else if (afterId != null) {
              const target = findNode(pruned, afterId, null);
              if (target) updated = insertAfterNode(pruned, afterId, target.type, dragged);
            } else {
              updated = folderId == null ? [dragged, ...pruned] : insertChildNode(pruned, folderId, 'folder', dragged);
            }
            return updated;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    deleteFolder: builder.mutation({
      query: ({ folderId, networkId }) => ({ url: `shared_list_folders/${folderId}`, method: 'DELETE', networkId }),
      invalidatesTags: (result, error) => (error ? LIST_TREE_TAG : []),
      async onQueryStarted({ folderId, networkId }, { dispatch, queryFulfilled }) {
        const updateFn = (draft) => removeNode(draft, folderId, 'folder');
        const patch = dispatch(listsTreeApi.util.updateQueryData('getListTree', { networkId }, updateFn));
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    bulkMoveListsToFolder: builder.mutation({
      query: ({ listIds, parentId, networkId }) => ({
        url: 'shared_lists/bulk_move_to_folder',
        method: 'PATCH',
        networkId,
        data: { sharedListIds: listIds, parentId },
      }),
      invalidatesTags: (result, error) => (error ? LIST_TREE_TAG : []),
      async onQueryStarted({ listIds, parentId, networkId }, { dispatch, queryFulfilled, getState }) {
        // Mirror backend semantics: reverse-iterate so input order maps to top-to-bottom
        // in the destination container.
        const updateListTree = (draft) => applyBulkMoveToTree(draft, listIds, parentId);
        const treePatch = dispatch(listsTreeApi.util.updateQueryData('getListTree', { networkId }, updateListTree));
        // Patch every active getSharedLists subscription so the Lists page Location
        // column updates immediately. Invalidation alone isn't enough — getSharedLists
        // uses uniqBy on merge for pagination, which can preserve stale rows when
        // refetching anything past page 1.
        const treeData = listsTreeApi.endpoints.getListTree.select({ networkId })(getState())?.data || [];
        const folderPath = buildFolderPath(treeData, parentId);
        const idSet = new Set(listIds);
        const listPatches = contactsApi.util
          .selectInvalidatedBy(getState(), [{ type: 'contacts-shared-lists' }])
          .filter(({ endpointName }) => endpointName === 'getSharedLists')
          .map(({ originalArgs }) => {
            const updateSharedLists = (draft) => {
              // Axios interceptor camelizes response keys, so the cached field
              // is folderPath, not folder_path.
              draft?.sharedLists?.forEach((list) => {
                if (idSet.has(list.id)) Object.assign(list, { folderPath });
              });
            };

            return dispatch(contactsApi.util.updateQueryData('getSharedLists', originalArgs, updateSharedLists));
          });
        try {
          await queryFulfilled;
          dispatch(contactsApi.util.invalidateTags(['contacts-shared-lists']));
        } catch {
          treePatch.undo();
          listPatches.forEach((p) => p.undo());
        }
      },
    }),
    bulkDestroySharedLists: builder.mutation({
      query: ({ listIds, networkId }) => ({
        url: 'shared_lists/bulk_destroy',
        method: 'DELETE',
        networkId,
        data: { sharedListIds: listIds },
      }),
      invalidatesTags: (result, error) => (error ? LIST_TREE_TAG : []),
      async onQueryStarted({ listIds, networkId }, { dispatch, queryFulfilled, getState }) {
        const updateListTree = (draft) => applyBulkDestroyToTree(draft, listIds);
        const treePatch = dispatch(listsTreeApi.util.updateQueryData('getListTree', { networkId }, updateListTree));
        const listPatches = contactsApi.util
          .selectInvalidatedBy(getState(), [{ type: 'contacts-shared-lists' }])
          .filter(({ endpointName }) => endpointName === 'getSharedLists')
          .map(({ originalArgs }) => {
            const updateSharedLists = (draft) => {
              removeSharedListsFromCache(draft, listIds);
            };

            return dispatch(contactsApi.util.updateQueryData('getSharedLists', originalArgs, updateSharedLists));
          });
        try {
          await queryFulfilled;
          dispatch(contactsApi.util.invalidateTags(['contacts-shared-lists']));
        } catch {
          treePatch.undo();
          listPatches.forEach((p) => p.undo());
        }
      },
    }),
  }),
});

export const {
  useGetListTreeQuery,
  useCreateFolderMutation,
  useUpdateFolderMutation,
  useMoveFolderMutation,
  useDeleteFolderMutation,
  useMoveListToFolderMutation,
  useBulkMoveListsToFolderMutation,
  useBulkDestroySharedListsMutation,
} = listsTreeApi;
